import { useCallback, useRef } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { pluralizeWord } from '@shared/helpers/formatters';
import { getUserProfileLink } from '@shared/helpers/user';
import { useApiQuery } from '@shared/hooks/useApiQuery';
import { useApiInfiniteQuery } from '@shared/hooks/useInfiniteQuery';
import { type AnnotatedApiQueryMethod } from '@shared/utils/makeApiQuery';

import { type GetApplicationApiResponsePayload, getUserActivity } from '@api/applications/general';

import { ReviewCard } from '@ui/uikit/components/common/cards/ReviewCard';
import { DialogButton } from '@ui/uikit/components/common/Dialog/DialogLayout/internals/DialogLayout';
import { Dialog } from '@ui/uikit/components/common/DialogV2';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';
import Spinner from '@ui/uikit/components/common/Loader/Loader';
import { Modal } from '@ui/uikit/components/common/Modal';

import type { FeedbackItem } from '../../ApplicationUserFeedbackCarousel';
import { feedbackKeyMap, type FeedbackType } from '../../ApplicationUserFeedbackCarousel';
import { ReviewReportingController } from '../../ReviewReportingController';
import { ReviewSharingControl, ReviewSharingController } from '../../ReviewSharingControl';
import styles from './ViewAllFeedbackItemsDialog.module.scss';

export interface ViewAllFeedbackItemsDialogProps {
  application: GetApplicationApiResponsePayload;
  isOpen: boolean;
  onClose: () => void;
  getItems: AnnotatedApiQueryMethod<[applicationSlug: string], FeedbackItem[]>;
  onAddFeedbackItem: () => void;
  feedbackType: FeedbackType;
  onHelpfulnessToggle: (selection: FeedbackButtonGroupSelection, id: number) => void;
  onFunnyReactionToggle: (state: 'on' | 'off', id: number) => void;
}

const ViewAllFeedbackItemsDialog = ({
                                      application,
                                      isOpen,
                                      onClose,
                                      onAddFeedbackItem,
                                      getItems,
                                      feedbackType,
                                      onHelpfulnessToggle,
                                      onFunnyReactionToggle,
                                    }: ViewAllFeedbackItemsDialogProps) => {
  const { app_id: applicationSlug, app_name: appName } = application;

  const { isAuth } = useAuth();
  const { data: userActivity } = useApiQuery(getUserActivity, [applicationSlug], {
    enabled: isAuth,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useApiInfiniteQuery(
    getItems,
    [applicationSlug],
    {},
    {
      enabled: isOpen,
    },
  );

  const items = data?.pages?.flat() || [];
  const itemsCount = items.length;

  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) {
        return;
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node) {
        return;
      }

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      observerRef.current.observe(node);

    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} overlays={['menus', 'modals']}>
      <Dialog variant="long" size="large">
        <Dialog.Header>{`${appName} ${pluralizeWord(feedbackType, itemsCount)}`}</Dialog.Header>
        <Dialog.Body>
          {items.map((feedbackItem) => {
            const { id, text, createTime, author, positive, negative, funny, rating } =
              feedbackItem;
            const reactions = { positive, negative, funny };
            const key = feedbackKeyMap[feedbackType];

            return (
              <div className={styles['self']} key={id}>
                <ReviewCard
                  size="large"
                  appName={appName}
                  excerpt={text}
                  date={new Date(createTime)}
                  feedbackType={feedbackType}
                  author={{
                    ...author,
                    profileLink: getUserProfileLink(author?.magicId),
                  }}
                  reactions={reactions}
                  rating={rating}
                  sharingControl={
                    <ReviewSharingController
                      appName={appName}
                      applicationSlug={applicationSlug}
                      feedbackType={feedbackType}
                      reviewId={id}
                    >
                      <ReviewSharingControl
                        author={author.name ?? undefined}
                        applicationName={application.app_name}
                        feedbackType={feedbackType}
                      />
                    </ReviewSharingController>
                  }
                  reportingControl={
                    <ReviewReportingController
                      applicationSlug={applicationSlug}
                      feedbackItem={feedbackItem}
                      feedbackType={feedbackType}
                      applicationName={application.app_name}
                    />
                  }
                  activeReactions={{
                    positive: userActivity?.payload?.[key]?.[id.toString()]?.vote?.vote === 1,
                    negative: userActivity?.payload?.[key]?.[id.toString()]?.vote?.vote === -1,
                    funny: !!userActivity?.payload?.[key]?.[id.toString()]?.vote?.funny,
                  }}
                  onHelpfulnessToggle={(selection) => onHelpfulnessToggle(selection, id)}
                  onFunnyReactionToggle={(state) => onFunnyReactionToggle(state, id)}
                />
              </div>
            );
          })}

          <div ref={loadMoreRef} style={{ height: 1 }} />

          {isFetchingNextPage && (
            <div className={styles['loading']}>
              <Spinner size="sm" color="light" />
            </div>
          )}
        </Dialog.Body>
        <Dialog.ButtonGroup minButtonWidth>
          <DialogButton variant="secondary" onClick={onClose}>
            Close
          </DialogButton>
          <DialogButton onClick={onAddFeedbackItem}>{`Add ${feedbackType}`}</DialogButton>
        </Dialog.ButtonGroup>
      </Dialog>
    </Modal>
  );
};

export { ViewAllFeedbackItemsDialog };
